import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { X, Loader2, Search, User, CheckCircle2, Trash2, ExternalLink, Phone, Bell, MessageSquare, AlertCircle, Send, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { fmtDateTimeIT } from "@/lib/format";
import { enablePush, getCurrentStatus as getPushStatus, sendTestPush } from "@/lib/pushClient";

export function ReminderDialog({ open, onClose, onSaved, clientId, clientName, appointment = false, initial }) {
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [apptType, setApptType] = useState("telefonico"); // "ufficio" | "telefonico"
  const [tgLinked, setTgLinked] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Cliente collegato
  const [linkedClientId, setLinkedClientId] = useState(null);
  const [linkedClient, setLinkedClient] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // === Notifiche ===
  const [notifyMinutesBefore, setNotifyMinutesBefore] = useState(null);
  const [notifyChannels, setNotifyChannels] = useState([]);
  const [notifyAsAlarm, setNotifyAsAlarm] = useState(false);
  const [pushSupported, setPushSupported] = useState(true);
  const [pushPermission, setPushPermission] = useState("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);
  const isEditing = !!initial?.id;

  // Check push capability + current status at mount AND when dialog opens
  // (lo status puo cambiare se l'utente attiva push in un'altra schermata)
  useEffect(() => {
    if (!open) return;
    getPushStatus().then((s) => {
      setPushSupported(s.supported);
      setPushPermission(s.permission || "default");
      setPushSubscribed(!!s.subscribed);
    }).catch(() => setPushSupported(false));
    api.get("/telegram/status").then((r) => setTgLinked(!!(r.data?.configured && r.data?.linked))).catch(() => setTgLinked(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setTitle(initial.title || "");
      setWhen(initial.when ? initial.when.slice(0, 16) : "");
      setApptType(initial.apptType || "telefonico");
      setNotes(initial.notes || "");
      setLinkedClientId(initial.clientId || clientId || null);
      setNotifyMinutesBefore(initial.notifyMinutesBefore != null ? initial.notifyMinutesBefore : null);
      setNotifyChannels(Array.isArray(initial.notifyChannels) ? initial.notifyChannels : []);
      setNotifyAsAlarm(!!initial.notifyAsAlarm);
    } else {
      const t = new Date();
      t.setMinutes(0, 0, 0);
      t.setHours(t.getHours() + 1);
      setTitle(appointment ? `Appuntamento ${clientName || ""}`.trim() : `Richiamo ${clientName || ""}`.trim());
      setWhen(`${t.toLocaleDateString("sv-SE")}T${String(t.getHours()).padStart(2, "0")}:00`);
      setNotes("");
      setLinkedClientId(clientId || null);
      // Default: notifica all'orario per promemoria, 15min prima per appuntamenti
      setNotifyMinutesBefore(appointment ? 15 : 0);
      // Push pre-selezionato SOLO se l'utente ha gia' una subscription valida.
      // Altrimenti niente push pre-selezionato (l'utente lo abilita manualmente).
      setNotifyChannels([]);
      setNotifyAsAlarm(false);
    }
    setShowSearch(false);
    setSearchQ("");
  }, [open, appointment, clientName, clientId, initial]);

  // Pre-seleziona push come default SOLO se l'utente ha gia' una subscription
  // (per creazione di un nuovo reminder, non in edit mode)
  useEffect(() => {
    if (!open || initial) return;
    if (pushSubscribed && pushSupported && pushPermission === "granted") {
      setNotifyChannels((arr) => arr.includes("push") ? arr : [...arr, "push"]);
    }
  }, [open, initial, pushSubscribed, pushSupported, pushPermission]);

  // Quando cambia linkedClientId, carico le info del cliente collegato
  useEffect(() => {
    if (!open || !linkedClientId) { setLinkedClient(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/clients/${linkedClientId}`);
        if (!cancelled) setLinkedClient(r.data);
      } catch { if (!cancelled) setLinkedClient(null); }
    })();
    return () => { cancelled = true; };
  }, [linkedClientId, open]);

  // Search debounced
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!showSearch || !searchQ || searchQ.length < 2) { setSearchResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/clients", { params: { q: searchQ, limit: 8 } });
        setSearchResults(r.data.items || []);
      } catch (e) {
        console.warn("Ricerca clienti fallita:", e?.response?.status || e?.message);
      } finally { setSearching(false); }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchQ, showSearch]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) return toast.error("Inserisci un titolo");
    if (!when) return toast.error("Inserisci data/ora");

    // Validazione PUSH: se l'utente ha selezionato push ma non c'e' una subscription
    // attiva, tentiamo di crearla ora. Se fallisce, rimuoviamo push dai channels
    // per evitare di salvare una notifica che non potra' mai essere inviata.
    let finalChannels = [...notifyChannels];
    if (finalChannels.includes("push") && !pushSubscribed) {
      try {
        await enablePush();
        setPushSubscribed(true);
        setPushPermission("granted");
      } catch (err) {
        toast.warning(err?.message || "Push non attivabile: salvo senza push");
        finalChannels = finalChannels.filter((c) => c !== "push");
      }
    }

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        when: when.slice(0, 16),
        notes,
        appointment,
        apptType: appointment ? apptType : null,
        clientId: linkedClientId || null,
        notifyMinutesBefore: finalChannels.length > 0 ? (notifyMinutesBefore ?? 0) : null,
        notifyChannels: finalChannels,
        notifyAsAlarm: finalChannels.includes("push") ? notifyAsAlarm : false,
      };
      if (initial?.id) await api.patch(`/reminders/${initial.id}`, payload);
      else await api.post("/reminders", payload);
      toast.success(appointment ? "Appuntamento salvato" : "Promemoria salvato");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const handleTestPush = async () => {
    try {
      if (!pushSubscribed) {
        await enablePush();
        setPushSubscribed(true);
        setPushPermission("granted");
      }
      await sendTestPush();
      toast.success("Notifica di test inviata — controlla il device");
    } catch (err) {
      toast.error(err?.message || "Test push fallito");
    }
  };

  const markCompleted = async () => {
    if (!initial?.id) return;
    try {
      await api.patch(`/reminders/${initial.id}`, { completed: !initial.completed });
      toast.success(initial.completed ? "Riaperto" : "Completato");
      onSaved?.();
    } catch { toast.error("Errore"); }
  };

  const remove = async () => {
    if (!initial?.id) return;
    if (!window.confirm("Eliminare definitivamente?")) return;
    try {
      await api.delete(`/reminders/${initial.id}`);
      toast.success("Eliminato");
      onSaved?.();
    } catch { toast.error("Errore"); }
  };

  const palette = appointment
    ? { accent: "#B7955B", header: "bg-[#B7955B] text-white", btn: "bg-[#B7955B] hover:bg-[#A18048]" }
    : { accent: "#0A192F", header: "bg-[#0A192F] text-white", btn: "bg-[#0A192F] hover:bg-[#172A45]" };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/65 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg overflow-hidden shadow-[0_24px_64px_rgba(10,25,47,0.25)] max-h-[92vh] flex flex-col"
        style={{ border: "1px solid #E8ECF2", borderRadius: 8 }}
        data-testid="reminder-dialog"
      >
        <div className={`${palette.header} px-6 py-4 flex items-center justify-between shrink-0`}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">{appointment ? "Appuntamento" : "Promemoria"}</div>
            <h3 className="font-serif text-2xl font-light">
              {isEditing ? (appointment ? "Modifica appuntamento" : "Modifica promemoria") : (appointment ? "Nuovo appuntamento" : "Nuovo promemoria")}
            </h3>
            {isEditing && initial?.when && <div className="text-[11px] opacity-80 mt-0.5">{fmtDateTimeIT(initial.when)}{initial.completed ? " · ✓ Completato" : ""}</div>}
          </div>
          <button type="button" onClick={onClose} className="opacity-80 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1 bg-white">
          {appointment && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Tipo appuntamento</label>
              <div className="mt-1 flex items-center gap-2" data-testid="rem-appt-type">
                <button type="button" onClick={() => setApptType("ufficio")} data-testid="rem-appt-ufficio"
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold border transition ${apptType === "ufficio" ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-500 border-slate-300 hover:border-slate-500"}`}>
                  <Building2 className="w-3.5 h-3.5" /> Ufficio
                </button>
                <button type="button" onClick={() => setApptType("telefonico")} data-testid="rem-appt-telefonico"
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold border transition ${apptType === "telefonico" ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-500 border-slate-300 hover:border-slate-500"}`}>
                  <Phone className="w-3.5 h-3.5" /> Telefonico
                </button>
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Titolo</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="rem-title" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-[#0A192F] focus:ring-2 focus:ring-slate-900/8" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Quando</label>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} required data-testid="rem-when" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm tabular-nums" />
          </div>

          {/* Cliente collegato */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente collegato (opzionale)</label>
            {linkedClient ? (
              <div className="mt-1 flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-sm bg-slate-50/50">
                <div className="w-7 h-7 rounded-full bg-[#0A192F] text-white text-xs flex items-center justify-center font-semibold shrink-0">
                  {(linkedClient.cognome || linkedClient.nome || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {linkedClient.cognome} {linkedClient.nome}
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2">
                    {linkedClient.telefono && <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{linkedClient.telefono}</span>}
                    {linkedClient.stato && <span className="bg-slate-200 px-1.5 py-0.5 rounded-sm">{linkedClient.stato}</span>}
                  </div>
                </div>
                <Link to={`/clienti/${linkedClient.id}`} className="text-[#B7955B] hover:text-[#0A192F]" title="Apri scheda" target="_blank">
                  <ExternalLink className="w-4 h-4" />
                </Link>
                <button type="button" onClick={() => setLinkedClientId(null)} className="text-slate-400 hover:text-rose-600" title="Scollega">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : showSearch ? (
              <div className="mt-1 relative">
                <div className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-sm focus-within:border-[#0A192F]">
                  <Search className="w-4 h-4 text-slate-400" />
                  <input
                    autoFocus
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="Cerca per nome, cognome, telefono…"
                    data-testid="rem-search-client"
                    className="flex-1 outline-none text-sm bg-transparent"
                  />
                  {searching && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                  <button type="button" onClick={() => setShowSearch(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
                </div>
                {searchResults.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-60 overflow-y-auto z-10">
                    {searchResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setLinkedClientId(c.id); setShowSearch(false); }}
                        data-testid={`rem-client-result-${c.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 border-b border-[#F0F2F5] last:border-b-0"
                      >
                        <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-900 truncate">{c.cognome} {c.nome}</div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                            {c.telefono && <span>{c.telefono}</span>}
                            {c.stato && <span>· {c.stato}</span>}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowSearch(true)}
                data-testid="rem-link-client"
                className="mt-1 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-dashed border-slate-300 rounded-sm hover:border-[#0A192F] hover:text-[#0A192F] text-slate-500"
              >
                <User className="w-4 h-4" /> Collega un'anagrafica
              </button>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Note</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rem-notes" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm resize-y" />
          </div>

          {/* === NOTIFICHE === */}
          <div className="border border-[#B7955B]/30 rounded-sm p-3 bg-gradient-to-br from-[#FBF8F1] to-white">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#8a6e3d] font-semibold mb-3 flex items-center justify-between gap-1.5">
              <span className="flex items-center gap-1.5 text-[#B7955B]"><Bell className="w-3.5 h-3.5" /> Notifiche</span>
              <button
                type="button"
                onClick={handleTestPush}
                data-testid="rem-test-push"
                disabled={!pushSupported || pushPermission === "denied"}
                title={pushSubscribed ? "Invia una notifica di test ora" : "Attiva push e invia un test"}
                className="text-[10px] normal-case tracking-normal flex items-center gap-1 px-2 py-1 rounded-sm border border-[#B7955B] text-[#8a6e3d] hover:bg-[#B7955B] hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-3 h-3" /> Test push
              </button>
            </div>
            {/* Banner di stato per chiarire all'utente cosa succede */}
            {!pushSupported && (
              <div className="mb-3 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Push notifiche non supportate da questo browser. Su iPhone serve aprire l'app come PWA (Aggiungi a schermata Home).</span>
              </div>
            )}
            {pushSupported && pushPermission === "denied" && (
              <div className="mb-3 flex items-start gap-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-sm p-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Notifiche bloccate. Apri le impostazioni del browser per questo sito e abilita le notifiche.</span>
              </div>
            )}
            {pushSupported && pushPermission !== "denied" && !pushSubscribed && (
              <div className="mb-3 flex items-start gap-2 text-[11px] text-slate-700 bg-slate-100 border border-slate-200 rounded-sm p-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#B7955B]" />
                <span>Push non ancora attive su questo device. Spunta "Push browser / PWA" o "Test push" per attivarle.</span>
              </div>
            )}
            {pushSubscribed && (
              <div className="mb-3 flex items-start gap-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm p-2">
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Push attive su questo device. Le riceverai anche con app chiusa (se installata come PWA).</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <label
                className={`flex items-start gap-2 text-xs p-2 bg-white border rounded-sm transition ${
                  !pushSupported || pushPermission === "denied"
                    ? "border-slate-200 opacity-60 cursor-not-allowed"
                    : "border-slate-200 hover:border-[#B7955B] cursor-pointer"
                }`}
                title={!pushSupported ? "Push non supportate dal browser" : pushPermission === "denied" ? "Notifiche bloccate — abilita dalle impostazioni del browser" : ""}
              >
                <input
                  type="checkbox"
                  checked={notifyChannels.includes("push")}
                  disabled={!pushSupported || pushPermission === "denied" || enablingPush}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setEnablingPush(true);
                      enablePush()
                        .then(() => {
                          setNotifyChannels((arr) => Array.from(new Set([...arr, "push"])));
                          setPushPermission("granted");
                          setPushSubscribed(true);
                          toast.success("Push notifiche attive su questo device");
                        })
                        .catch((err) => {
                          toast.error(err?.message || "Impossibile attivare le push");
                          // Aggiorna stato per riflettere il rifiuto
                          getPushStatus().then((s) => {
                            setPushPermission(s.permission || "default");
                            setPushSubscribed(!!s.subscribed);
                          });
                        })
                        .finally(() => setEnablingPush(false));
                    } else {
                      setNotifyChannels((arr) => arr.filter((c) => c !== "push"));
                    }
                  }}
                  data-testid="rem-notify-push"
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 flex items-center gap-1">
                    <Bell className="w-3 h-3 text-[#0A192F]" /> Push browser / PWA
                    {enablingPush && <Loader2 className="w-3 h-3 animate-spin text-[#B7955B]" />}
                    {pushSubscribed && !enablingPush && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {!pushSupported && "Non disponibile su questo browser"}
                    {pushSupported && pushPermission === "denied" && "Bloccate — sblocca dal browser"}
                    {pushSupported && pushPermission !== "denied" && !pushSubscribed && "Tocca per attivare (richiede permesso)"}
                    {pushSubscribed && "Attive — notifica nativa sul telefono"}
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2 text-xs cursor-pointer p-2 bg-white border border-slate-200 rounded-sm hover:border-emerald-400 transition">
                <input
                  type="checkbox"
                  checked={notifyChannels.includes("whatsapp")}
                  onChange={(e) => {
                    setNotifyChannels((arr) => e.target.checked
                      ? Array.from(new Set([...arr, "whatsapp"]))
                      : arr.filter((c) => c !== "whatsapp"));
                  }}
                  data-testid="rem-notify-wa"
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3 text-emerald-600" /> WhatsApp al tuo numero
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">Messaggio precompilato in agenda</div>
                </div>
              </label>
              <label className="flex items-start gap-2 text-xs cursor-pointer p-2 bg-white border border-slate-200 rounded-sm hover:border-sky-400 transition">
                <input
                  type="checkbox"
                  checked={notifyChannels.includes("telegram")}
                  onChange={(e) => {
                    setNotifyChannels((arr) => e.target.checked
                      ? Array.from(new Set([...arr, "telegram"]))
                      : arr.filter((c) => c !== "telegram"));
                  }}
                  data-testid="rem-notify-telegram"
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 flex items-center gap-1">
                    <Send className="w-3 h-3 text-sky-600" /> Telegram (automatico)
                    {tgLinked && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {tgLinked ? "Arriva da solo sulla tua chat" : <>Da collegare nel <a href="/profilo" className="underline text-sky-600">Profilo</a></>}
                  </div>
                </div>
              </label>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1 font-semibold">Avvisami</label>
              <select
                value={notifyMinutesBefore == null ? "" : String(notifyMinutesBefore)}
                onChange={(e) => setNotifyMinutesBefore(e.target.value === "" ? null : parseInt(e.target.value))}
                data-testid="rem-notify-when"
                disabled={notifyChannels.length === 0}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm bg-white disabled:opacity-50 disabled:bg-slate-50"
              >
                <option value="0">All'orario esatto</option>
                <option value="5">5 minuti prima</option>
                <option value="15">15 minuti prima</option>
                <option value="30">30 minuti prima</option>
                <option value="60">1 ora prima</option>
                <option value="120">2 ore prima</option>
                <option value="1440">1 giorno prima</option>
                <option value="2880">2 giorni prima</option>
              </select>
            </div>
            {/* === SVEGLIA: visibile solo se Push e' attivo === */}
            {notifyChannels.includes("push") && (
              <label
                className={`mt-3 flex items-start gap-2 text-xs p-2.5 bg-white border rounded-sm cursor-pointer transition ${
                  notifyAsAlarm
                    ? "border-rose-400 ring-1 ring-rose-200"
                    : "border-slate-200 hover:border-rose-300"
                }`}
                data-testid="rem-alarm-toggle"
              >
                <input
                  type="checkbox"
                  checked={notifyAsAlarm}
                  onChange={(e) => setNotifyAsAlarm(e.target.checked)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                    <span className="text-base leading-none">🚨</span>
                    Modalità Sveglia
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                    Vibrazione intensa + notifica persistente con tasti "Rimanda 5/15 min" e "OK". Ideale per appuntamenti importanti che non devi assolutamente mancare.
                  </div>
                </div>
              </label>
            )}
            {notifyChannels.includes("whatsapp") && (
              <div className="mt-2 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm p-2 flex items-start gap-1.5">
                <MessageSquare className="w-3 h-3 shrink-0 mt-0.5" />
                <span>Il messaggio sarà pronto in <Link to="/agenda" className="font-semibold underline">Agenda</Link> al momento giusto, con 1 click si apre WhatsApp.</span>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-between gap-2 bg-slate-50 shrink-0">
          <div className="flex items-center gap-2">
            {isEditing && (
              <>
                <button type="button" onClick={markCompleted} data-testid="rem-toggle-completed" className="px-3 py-2 text-xs border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-sm flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {initial.completed ? "Riapri" : "Completa"}
                </button>
                <button type="button" onClick={remove} data-testid="rem-delete" className="px-3 py-2 text-xs border border-rose-300 text-rose-700 hover:bg-rose-50 rounded-sm flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Elimina
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white text-slate-700">Annulla</button>
            <button type="submit" disabled={saving} data-testid="rem-submit" className={`px-4 py-2 text-sm text-white rounded-sm flex items-center gap-2 disabled:opacity-60 ${palette.btn}`}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
